import { NgModule } from "@angular/core";
import { RouterModule, Routes } from "@angular/router";
import { LoginComponent } from "./components/login/login.component";
import { MainScreenComponent } from "./components/frames/main-screen/main-screen.component";
import { AuthenticationGuard } from "./helper/authentication.guard";
import { BranchesComponent } from "./components/branch/branches/branches.component";
import { BranchDetailsComponent } from "./components/branch/branch-details/branch-details.component";
import { DashboardComponent } from "./components/dashboard/dashboard.component";
import { MenuComponent } from "./components/menu/menu.component";
import { MobileMenuComponent } from "./components/mobile-menu/mobile-menu.component";
import { UsersComponent } from "./components/user/users/users.component";
import { UserDetailsComponent } from "./components/user/user-details/user-details.component";
import { DepartmentsComponent } from "./components/department/departaments/departments.component";
import { DepartmentsDetailsComponent } from "./components/department/departments-details/departments-details.component";
import { PriorityListComponent } from "./components/priority/priority/priority.component";
import { PriorityDetailsComponent } from "./components/priority/priority-details/priority-details.component";
import { StatusComponent } from "./components/status/status/status.component";
import { StatusDetailsComponent } from "./components/status/status-details/status-details.component";
import { TagsComponent } from "./components/tags/tags/tags.component";
import { TagsDetailsComponent } from "./components/tags/tags-details/tags-details.component";
import { TicketDetailsComponent } from "./components/ticket/ticket-details/ticket-details.component";
import { RecurrenceChangePanelComponent } from "./components/recurrence/recurrence-change-panel/recurrence-change-panel.component";
import { RecurrenceCancelPanelComponent } from "./components/recurrence/recurrence-cancel-panel/recurrence-cancel-panel.component";
import { TicketComponent } from "./components/ticket/ticket/ticket.component";
import { SingleChoiceComponent } from "./components/singlechoice/singlechoice/singlechoice.component";
import { CommentsComponent } from "./components/comments/comments.component";
import { CommentsDetailsComponent } from "./components/comments-details/comments-details.component";
import { TaskComponent } from "./components/task/task.component";
import { TaskDetailsComponent } from "./components/task-details/task-details.component";

import { InvolvedComponent } from "./components/involved/involved/involved.component";
import { InvolvedDetailsComponent } from "./components/involved/involved-details/involved-details.component";
import { MediaListComponent } from "./components/media-list/media-list.component";
import { MediaDetailsComponent } from "./components/media-details/media-details.component";
import { FilterForTicketComponent } from "./components/filters/filter-for-ticket/filter-for-ticket/filter-for-ticket.component";
import { FilterFormComponent } from "./components/filters/filters-forms/filter-form/filter-form.component";
import { DepartmentUserComponent } from "./components/department/department-user/department-user.component";
import { DepartmentUserDetailsComponent } from "./components/department/department-user-details/department-user-details.component";
import { MultiActionComponent } from "./components/multi-action/multi-action.component";
import { ProcessingComponent } from "./components/multi-action/processing/processing.component";
import { ForgotPasswordComponent } from "./components/forgot-password/forgot-password.component";
import { ResetPasswordComponent } from "./components/reset-password/reset-password.component";
import { VersionsComponent } from "./components/versions/versions.component";
import { TicketTimelineComponent } from "./components/ticket-timeline/ticket-timeline.component";
import { DebugPanelComponent } from "./components/debug-panel/debug-panel.component";

export const routes: Routes = [
  { path: "login", component: LoginComponent },
  { path: "forgot-password", component: ForgotPasswordComponent },
  { path: "reset-password/:token", component: ResetPasswordComponent },
  {
    path: "",
    component: MainScreenComponent,
    canActivate: [AuthenticationGuard],
    children: [
      // The ticket list is the start page: entering the app on "/" - after a
      // login, from the Android app or by typing the bare url - lands there
      // instead of on an empty screen.
      { path: "", redirectTo: "tickets", pathMatch: "full" },
      { path: "instellingen", component: DashboardComponent },
      // Old links still point at /dashboard; drop this once none are left.
      { path: "dashboard", redirectTo: "instellingen", pathMatch: "full" },
      { path: "branches", component: BranchesComponent },
      { path: "branches/:id", component: BranchDetailsComponent },
      { path: "menu", component: MenuComponent },
      { path: "mobile_menu", component: MobileMenuComponent },
      { path: "users", component: UsersComponent },
      { path: "users/:id", component: UserDetailsComponent },
      { path: "users/new", component: UserDetailsComponent },
      { path: "departments", component: DepartmentsComponent },
      { path: "departments/new", component: DepartmentsDetailsComponent },
      { path: "departments/:id", component: DepartmentsDetailsComponent },
      {
        path: "departments/:id/fragment/:topic",
        component: DepartmentsDetailsComponent,
      },
      { path: "priority", component: PriorityListComponent },
      { path: "priority/:id", component: PriorityDetailsComponent },
      { path: "status", component: StatusComponent },
      { path: "status/:id", component: StatusDetailsComponent },
      { path: "tags", component: TagsComponent },
      { path: "tags/:id", component: TagsDetailsComponent },
      { path: "tickets", component: TicketComponent },
      { path: "tickets/by-branch/:branchId", component: TicketComponent },
      { path: "tickets/by-branch/:branchId/page/:page", component: TicketComponent },

      { path: "tickets/:id/filter", component: FilterFormComponent },
      {
        path: "tickets/:id/filter/:filterCode",
        component: FilterFormComponent,
      },
      {
        path: "tickets/:id/filter/:filterCode/:filterid",
        component: FilterFormComponent,
      },

      { path: "tickets/:id", component: TicketDetailsComponent },
      { path: "tickets/:id/edit", component: TicketDetailsComponent },
      {
        path: "tickets/:id/result/page/:pageid",
        component: TicketComponent,
      },
      // the recurrence questions are panels of their own, not a pick list
      {
        path: "tickets/:id/choose/recurrence-change",
        component: RecurrenceChangePanelComponent,
      },
      {
        path: "tickets/:id/choose/recurrence-cancel",
        component: RecurrenceCancelPanelComponent,
      },
      { path: "tickets/:id/choose/:topic", component: SingleChoiceComponent },

      { path: "task/:id/fragment/:topic", component: SingleChoiceComponent },
      { path: "comment", component: CommentsComponent },
      { path: "comment/:id", component: CommentsDetailsComponent },
      {
        path: "tickets/:id/fragmentedit/comment/:commentId",
        component: CommentsDetailsComponent,
      },
      // Every tab other than 'comment' needs a route as well. Without this
      // line 'fragmentedit/media/...', 'fragmentedit/involved/...' or
      // 'fragmentedit/timeline/...' match nothing at all, the navigation fails
      // silently and the screen is left half way. It comes after the comment
      // line, so that a comment keeps going to the comment screen.
      {
        path: "tickets/:id/fragmentedit/:topic/:tid",
        component: TicketDetailsComponent,
      },
      {
        path: "tickets/:id/fragment/recurrence",
        component: TicketDetailsComponent,
      },
      {
        path: "tickets/:id/fragment/:topic",
        component: TicketDetailsComponent,
      },
      {
        path: "tickets/:id/fragment/:topic/:tid",
        component: TicketDetailsComponent,
      },
      { path: "task", component: TaskComponent },
      { path: "task/:id", component: TaskDetailsComponent },
      {
        path: "tickets/:id/fragmentedit/task/:taskId",
        component: TaskDetailsComponent,
      },
      {
        path: "tickets/:id/multi-process/action/:id",
        component: ProcessingComponent,
      },
      { path: "involved", component: InvolvedComponent },
      { path: "involved/:id", component: InvolvedDetailsComponent },
      { path: "department-user", component: DepartmentUserComponent },
      {
        path: "departments/:id/fragment/department-user",
        component: DepartmentUserComponent,
      },
      {
        path: "departments/:id/fragmentedit/department-user/:dId",
        component: DepartmentUserDetailsComponent,
      },

      {
        path: "tickets/:id/fragmentedit/involved/:involvedId",
        component: InvolvedDetailsComponent,
      },
      { path: "media", component: MediaListComponent },
      { path: "media/:id", component: MediaDetailsComponent },
      {
        path: "tickets/:id/fragmentedit/media/:mediaId",
        component: MediaDetailsComponent,
      },
      { path: "multi-action", component: MultiActionComponent },
      { path: "multi-action/:id", component: MultiActionComponent },
      { path: "processing", component: ProcessingComponent },
      { path: "version", component: VersionsComponent },
      { path: "time-line", component: TicketTimelineComponent },
      { path: "debug-painel", component: DebugPanelComponent},
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
